import React from "react";
import Layout from "../Layouts/layout";
import "./orderKit.scss";
import { fetchData, SubmitData } from "../../../services/apiService";
import { useNavigate } from "react-router-dom";
import useAlertUtils from "../../../utils/common.utils";

function KitOrders() {
  const navigate = useNavigate();
  const { alertSuccess, alertError } = useAlertUtils();
  const [allOrders, setAllOrders] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [search, setSearch] = React.useState("");
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [isActionLoading, setIsActionLoading] = React.useState(false);

  const filteredOrders = React.useMemo(() => {
    if (!search.trim()) return allOrders;

    const searchLower = search.toLowerCase();
    return allOrders.filter(
      (order) =>
        (order.id && order.id.toString().toLowerCase().includes(searchLower)) ||
        (order.attentionTo &&
          order.attentionTo.toLowerCase().includes(searchLower)) ||
        (order.trackingEmail &&
          order.trackingEmail.toLowerCase().includes(searchLower)) ||
        (order.shippingMethodCode &&
          order.shippingMethodCode.toLowerCase().includes(searchLower))
    );
  }, [allOrders, search]);

  const totalCount = filteredOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const orders = filteredOrders.slice(startIndex, endIndex);

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const tokenData = JSON.parse(atob(token.split(".")[1]));
      return tokenData.userId || tokenData.id || null;
    } catch (e) {
      console.warn("Could not parse token:", e);
      return null;
    }
  };

  const isSuperAdmin = React.useMemo(() => {
    const id = getUserIdFromToken();
    return String(id) === "69";
  }, []);

  const loadOrders = React.useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const userId = getUserIdFromToken();
      if (!userId) {
        setError("Unable to get user information");
        setAllOrders([]);
        return;
      }

      const res = await fetchData(`/api/kit-orders/get-kit-orders/${userId}`);
      const list = res?.data?.items || res?.data || [];
      setAllOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      setError("Failed to load orders");
      setAllOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const onRowClick = (order) => {
    if (!isSuperAdmin) return;
    setSelectedOrder(order);
  };

  const closeModal = () => {
    if (isActionLoading) return;
    setSelectedOrder(null);
  };

  const performAction = async (action) => {
    if (!selectedOrder?.id) return;
    try {
      setIsActionLoading(true);
      const status = action === "approve" ? "accepted" : "rejected";
      const res = await SubmitData("/api/kit-orders/order-status", {
        orderId: selectedOrder.id,
        status: status,
        orderData: selectedOrder,
      });
      if (res?.success || res?.status === true) {
        alertSuccess(`Order ${action}ed successfully`);
        await loadOrders();
        setSelectedOrder((prev) =>
          prev
            ? {
                ...prev,
                status: action === "approve" ? "confirmed" : "rejected",
              }
            : prev
        );
      } else {
        alertError(res?.message || `Failed to ${action} order`);
      }
    } catch (e) {
      alertError(`Failed to ${action} order`);
    } finally {
      setIsActionLoading(false);
      closeModal();
    }
  };

  const clearSearch = () => {
    setSearch("");
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  return (
    <Layout>
      <div className="testsPage">
        <div className="topBar">
          <div className="topBarLeft">
            <h1 className="pageTitle">Kit Orders</h1>
          </div>
          <div className="topBarRight">
            <div className="searchControl">
              <div className="searchBar">
                <span className="searchIcon">🔎</span>
                <input
                  className="searchInput"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by user, email, order id"
                />
                {search && (
                  <button className="clearSearch" onClick={clearSearch}>
                    ×
                  </button>
                )}
              </div>
            </div>
            <button
              className="newOrderBtn"
              onClick={() => navigate("/app/order-kit")}
            >
              + New Order
            </button>
          </div>
        </div>

        <div className="ordersCard">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : error ? (
            <div className="emptyState">
              <h3>{error}</h3>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="emptyState">
              <h3>No orders found</h3>
              <p>
                {search
                  ? "Try adjusting your search or check back later."
                  : "No orders available."}
              </p>
            </div>
          ) : (
            <div className="tableWrapper">
              <table className="ordersTable">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Paid By</th>
                    <th>Provider Name</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Kits</th>
                    <th>Shipping Method</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, idx) => {
                    let kits = [];
                    try {
                      if (typeof o.kits === "string") {
                        kits = JSON.parse(o.kits);
                      } else if (Array.isArray(o.kits)) {
                        kits = o.kits;
                      }
                    } catch (e) {
                      console.warn("Failed to parse kits:", e);
                    }

                    const getStatusClass = (status) => {
                      if (!status) return "status-unknown";
                      const statusUpper = status.toUpperCase();
                      if (statusUpper === "CONFIRMED") {
                        return "status-confirmed";
                      } else if (statusUpper === "PENDING") {
                        return "status-pending";
                      } else if (statusUpper === "CANCELLED") {
                        return "status-cancelled";
                      } else if (statusUpper === "PAYMENT_AUTHORIZED") {
                        return "status-authorized";
                      }
                      return "status-unknown";
                    };

                    return (
                      <tr
                        key={idx}
                        className={isSuperAdmin ? "clickableRow" : ""}
                        onClick={() => onRowClick(o)}
                        title={
                          isSuperAdmin ? "View and moderate order" : undefined
                        }
                      >
                        <td>{o.id || "-"}</td>
                        <td>
                          <span
                            className={`orderType ${
                              o.paidBy === "user"
                                ? "orderType-user"
                                : "orderType-provider"
                            }`}
                          >
                            {o.paidBy === "user" ? "User" : "Provider"}
                          </span>
                        </td>
                        <td>{o.providerName || "-"}</td>
                        <td>{o.attentionTo || "-"}</td>
                        <td>{o.trackingEmail || "-"}</td>
                        <td>
                          {kits.length > 0 ? (
                            <div className="kitsList">
                              {kits.map((kit, kitIdx) => (
                                <div key={kitIdx} className="kitItem">
                                  <span className="kitName">
                                    {kit.name || kit.code || "Unknown Kit"}
                                  </span>
                                  {kit.code && (
                                    <span className="kitCode">
                                      ({kit.panels[0].code})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{o.shippingMethodCode || "-"}</td>
                        <td>
                          ${typeof o.total === "number"
                            ? `$${o.total.toFixed(2)}`
                            : o.total || "-"}
                        </td>
                        <td>
                          <span
                            className={`status ${getStatusClass(o.status)}`}
                          >
                            {o.status || "-"}
                          </span>
                        </td>
                        <td>
                          {o.createdAt
                            ? new Date(o.createdAt).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="paginationBar">
            <button
              className="pageBtn"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="pageInfo">
              Page {page} of {totalPages}
            </span>
            <button
              className="pageBtn"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isSuperAdmin && selectedOrder && (
        <div className="modalOverlay" onClick={closeModal}>
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h3>
                <span className="badge badge-id">
                  Order #{selectedOrder.id || "-"}
                </span>
              </h3>
              <button
                className="modalClose"
                onClick={closeModal}
                disabled={isActionLoading}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <div className="statsRow">
                <div className="statCard">
                  <div className="statLabel">Paid By</div>
                  <div className="statValue {selectedOrder.paidBy === 'user' ? 'user' : 'provider'}">
                    {selectedOrder.paidBy === "user" ? "User" : "Provider"}
                  </div>
                </div>
                <div className="statCard">
                  <div className="statLabel">Status</div>
                  <div
                    className={`statPill ${
                      selectedOrder.status?.toLowerCase().includes("confirmed")
                        ? "ok"
                        : selectedOrder.status
                            ?.toLowerCase()
                            .includes("pending")
                        ? "pending"
                        : "neutral"
                    }`}
                  >
                    {selectedOrder.status || "-"}
                  </div>
                </div>
                <div className="statCard">
                  <div className="statLabel">Total</div>
                  <div className="statValue">
                    ${typeof selectedOrder.total === "number"
                      ? `$${selectedOrder.total.toFixed(2)}`
                      : selectedOrder.total || "-"}
                  </div>
                </div>
              </div>

              <div className="orderDetailsGrid">
                <div>
                  <div className="detailItem">
                    <strong>Attention To</strong>
                    <div className="metaValue">
                      {selectedOrder.attentionTo || "-"}
                    </div>
                  </div>
                  <div className="detailItem">
                    <strong>Email</strong>
                    <div className="metaValue">
                      {selectedOrder.trackingEmail || "-"}
                    </div>
                  </div>
                  <div className="detailItem">
                    <strong>Contact</strong>
                    <div className="metaValue">
                      {selectedOrder.contactNo || "-"}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="detailItem">
                    <strong>Shipping</strong>
                    <div className="metaValue">
                      {selectedOrder.shippingMethodCode || "-"}
                    </div>
                  </div>
                  <div className="detailItem">
                    <strong>Created</strong>
                    <div className="metaValue">
                      {selectedOrder.createdAt
                        ? new Date(selectedOrder.createdAt).toLocaleString()
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="detailItem addressItem">
                <strong>Address</strong>
                <div className="addressValue">
                  <div className="line">
                    {[
                      selectedOrder.addressLine1,
                      selectedOrder.addressLine2,
                      selectedOrder.addressLine3,
                    ]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </div>
                  <div className="line muted">
                    {[
                      selectedOrder.city,
                      selectedOrder.region,
                      selectedOrder.countryCode,
                      selectedOrder.postalCode,
                    ]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </div>
                </div>
              </div>

              <div className="divider" />

              <div className="kitsSection">
                <div className="kitsHeader">
                  <h4>Kits</h4>
                </div>
                {(() => {
                  let kits = [];
                  try {
                    if (typeof selectedOrder.kits === "string") {
                      kits = JSON.parse(selectedOrder.kits);
                    } else if (Array.isArray(selectedOrder.kits)) {
                      kits = selectedOrder.kits;
                    }
                  } catch (e) {}
                  if (!kits || kits.length === 0)
                    return <div className="emptyState">No kits</div>;
                  return (
                    <div className="kitsList large">
                      {kits.map((k, i) => (
                        <div key={i} className="kitItem">
                          <span className="kitName">
                            {k.name || k.code || "Unknown Kit"}
                          </span>
                          {k.panels?.[0]?.code && (
                            <span className="kitCode">
                              ({k.panels[0].code})
                            </span>
                          )}
                          <span className="spacer" />
                          {k.quantity && (
                            <span className="kitQty">x{k.quantity}</span>
                          )}
                          {typeof k.price === "number" && (
                            <span className="kitPrice">
                              ${k.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="btn btn-reject"
                disabled={
                  isActionLoading ||
                  selectedOrder?.status !== "PAYMENT_AUTHORIZED"
                }
                onClick={() => performAction("reject")}
              >
                {isActionLoading ? "Working..." : "Reject"}
              </button>
              <button
                className="btn btn-approve"
                disabled={
                  isActionLoading ||
                  selectedOrder?.status !== "PAYMENT_AUTHORIZED"
                }
                onClick={() => performAction("approve")}
              >
                {isActionLoading ? "Working..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default KitOrders;
